import { Component, OnInit, inject, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { Drone } from '../../../core/data-models/drone.model';
import { DroneService } from '../../../core/data-services/drone.service';
import { clearableFieldValue } from '../../../core/utils/drone-request-utils';
import { CreateOrganizationDroneDto } from '../../../core/request-models/create-organization-drone-dto.model';
import { DroneStatus } from '../../../core/data-models/drone-status.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

@Component({
  selector: 'app-drone-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, MaxLengthDirective],
  templateUrl: './drone-form.component.html',
  styleUrls: ['./drone-form.component.scss']
})
export class DroneFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private droneService = inject(DroneService);

  form!: FormGroup;
  isEditMode = false;
  droneId: string | null = null;
  isSaving = false;
  /** The drone as loaded for editing; optional fields are compared with it so only real changes are sent. */
  private originalDrone?: Drone;

  // Selected manufacturer for cascading dropdown
  selectedManufacturerId = signal<string>('');

  // Available manufacturers from service
  readonly manufacturers = computed(() => this.droneService.manufacturers());

  // Models filtered by selected manufacturer
  readonly filteredModels = computed(() => {
    const mfrId = this.selectedManufacturerId();
    if (!mfrId) return [];
    return this.droneService.getModelsByManufacturer(mfrId);
  });

  // Status options
  readonly statusOptions: { value: DroneStatus; label: string }[] = [
    { value: 'active', label: 'Active' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'retired', label: 'Retired' }
  ];

  ngOnInit(): void {
    this.initForm();
    this.loadLookupData();
    this.checkEditMode();
  }

  private async loadLookupData(): Promise<void> {
    // Load manufacturers and models from API
    await this.droneService.loadLookups();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      manufacturerId: ['', [Validators.required]],
      modelId: [{ value: '', disabled: true }, [Validators.required]],
      serialNumber: ['', [Validators.required]],
      registrationNumber: [''],
      remoteId: [''],
      firmwareVersion: [''],
      status: ['active']
    });

    // Watch for manufacturer changes to update filtered models and enable/disable model control
    this.form.get('manufacturerId')?.valueChanges.subscribe(value => {
      this.selectedManufacturerId.set(value || '');
      const modelControl = this.form.get('modelId');

      if (value) {
        modelControl?.enable();
      } else {
        modelControl?.disable();
      }

      // Reset model selection when manufacturer changes (but not during initial load)
      if (!this.isEditMode || modelControl?.dirty) {
        modelControl?.setValue('');
      }
    });
  }

  private checkEditMode(): void {
    this.droneId = this.route.snapshot.paramMap.get('id');
    if (this.droneId) {
      this.isEditMode = true;
      this.loadDrone(this.droneId);
    }
  }

  private loadDrone(id: string): void {
    const drone = this.droneService.getDroneById(id);

    if (!drone) {
      // Drone not found, redirect to list
      this.router.navigate(['/drones']);
      return;
    }
    this.originalDrone = drone;

    // Cannot edit ADN drones
    if (drone.ownership === 'adn') {
      this.router.navigate(['/drones', id, 'view']);
      return;
    }

    // Set manufacturer first (to populate models dropdown)
    this.selectedManufacturerId.set(drone.manufacturerId ?? '');

    // Enable modelId control since we have a manufacturer
    this.form.get('modelId')?.enable();

    // Then patch form values
    this.form.patchValue({
      name: drone.name,
      manufacturerId: drone.manufacturerId,
      modelId: drone.modelId,
      serialNumber: drone.serialNumber,
      registrationNumber: drone.registrationNumber || '',
      remoteId: drone.remoteId || '',
      firmwareVersion: drone.firmwareVersion || '',
      status: drone.status
    });
  }

  onManufacturerChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedManufacturerId.set(select.value);
    // Reset model when manufacturer changes
    this.form.patchValue({ modelId: '' });
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSaving = true;

    try {
      const formData = this.form.getRawValue();
      const dto: CreateOrganizationDroneDto = {
        name: formData.name,
        manufacturerId: formData.manufacturerId,
        modelId: formData.modelId,
        serialNumber: formData.serialNumber,
        registrationNumber: clearableFieldValue(formData.registrationNumber, this.originalDrone?.registrationNumber),
        remoteId: clearableFieldValue(formData.remoteId, this.originalDrone?.remoteId),
        firmwareVersion: clearableFieldValue(formData.firmwareVersion, this.originalDrone?.firmwareVersion),
        status: formData.status
      };

      if (this.isEditMode && this.droneId) {
        await this.droneService.updateDrone(this.droneId, dto);
      } else {
        await this.droneService.createDrone(dto);
      }

      this.router.navigate(['/drones']);
    } catch (error: any) {
      console.error('[DroneFormComponent] Error saving drone:', error);
      alert(error.message || 'Failed to save drone');
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.router.navigate(['/drones']);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  // Get selected manufacturer name for summary
  getSelectedManufacturerName(): string {
    const mfrId = this.form.get('manufacturerId')?.value;
    if (!mfrId) return '';
    return this.droneService.getManufacturerName(mfrId);
  }

  // Get selected model name for summary
  getSelectedModelName(): string {
    const modelId = this.form.get('modelId')?.value;
    if (!modelId) return '';
    return this.droneService.getModelName(modelId);
  }
}
